import React, {
  ComponentType,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';
import { noop, useDeprecate } from '@adsk/alloy-react-helpers';

import { warnLegacySetEnabled } from './helpers/warnLegacySetEnabled';
import {
  SubscriptionStore,
  useSubscriptionsStore,
} from './stores/SubscriptionsStore';
import { Subscription } from './types';

type SubscriptionsContextValue = {
  data: null | Subscription[];
  error: null | unknown;
  loading: boolean;
  retry(): void;
  setEnabled?: (enabled: boolean) => void;
};

/**
 * Context for current user subscriptions.
 */
const SubscriptionsContext =
  getOrCreateGlobalContext<SubscriptionsContextValue>(
    CONTEXT_NAMES.SubscriptionsContext,
  );

const useSubscriptionsStoreWithValidation = () =>
  useStoreWithValidation<SubscriptionStore>(
    CONTEXT_NAMES.SubscriptionsContext,
    {
      hook: 'useSubscriptions',
      provider: 'SubscriptionsProvider',
    },
  );

export const useSubscriptions = (enable = true) => {
  useDeprecate(
    true,
    'SubscriptionsContext',
    'useSubscriptions',
    'useSubscriptionsConstruction',
  );
  const {
    fetchData,
    data: dataResponse,
    error,
    loading,
    retry,
  } = useSubscriptionsStoreWithValidation();

  useEffect(() => {
    if (enable) {
      fetchData();
    }
  }, [enable, fetchData]);

  return {
    data: error?.status === 401 ? null : (dataResponse?.results ?? null),
    loading: enable && loading,
    error,
    retry,
    setEnabled: noop,
  };
};

export const useSubscriptionsProvider = () => {
  const {
    data: dataResponse,
    error,
    loading,
    retry,
    fetchData,
  } = useSubscriptionsStore();

  // Trigger fetch on setEnabled for legacy provider backwards compatibility
  const legacySetEnabled = useCallback(() => {
    warnLegacySetEnabled(CONTEXT_NAMES.SubscriptionsContext);
    fetchData();
  }, [fetchData]);

  const data = error?.status === 401 ? null : (dataResponse?.results ?? null);

  return useMemo(
    () => ({
      data,
      loading,
      error,
      retry,
      setEnabled: legacySetEnabled,
    }),
    [data, loading, error, retry, legacySetEnabled],
  );
};

type SubscriptionsProviderProps = {
  children?: ReactNode;
};

export const SubscriptionsProvider: ComponentType<
  SubscriptionsProviderProps
> = ({ children }) => {
  const value = useSubscriptionsProvider();

  return (
    <SubscriptionsContext.Provider value={value}>
      {children}
    </SubscriptionsContext.Provider>
  );
};

SubscriptionsProvider.displayName = 'SubscriptionsProvider';
